import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { allBookings, deleteBookings } from '../../slice/bookingSlice'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { selectGlobalLoading } from '../../store/selectors/loadingSelector'
import { bookingListing } from '../../apis/apiHandler'
import DateRangePicker from "react-bootstrap-daterangepicker";
import Globals from '../../common/global'
import { exportExcelFile } from '../../common/utils'

const BookingList = () => {
    const dispatch = useDispatch();
    const { bookingList } = useSelector(state => state.booking);
    const isLoading = useSelector(selectGlobalLoading);
    const navigate = useNavigate();

    const {
        page,
        setPage,
        limit,
        setLimit,
        filter: statusFilter,
        setFilter: setStatusFilter,
        search: searchQuery,
        setSearch: setSearchQuery,
        filters = { rangDate: { start_date: '', end_date: '' } },
        setFilters,
    } = useModuleSettings('bookings', { filter: '', filters: { rangDate: { start_date: '', end_date: '' } } });

    const [searchDebounce, setSearchDebounce] = useState(searchQuery)
    const isMounted = useRef(false)

    const setDates = (e, { startDate, endDate }) => {
        setFilters((prev) => ({
            ...prev,
            rangDate: {
                start_date: moment(startDate)._i !== 0 ? moment(startDate).format('YYYY-MM-DD') : '',
                end_date: moment(endDate)._i !== 0 ? moment(endDate).format('YYYY-MM-DD') : ''
            },
        }));
    }

    const handleResetFilters = () => {
        setSearchQuery('')
        setStatusFilter('')
        setFilters({ rangDate: { start_date: '', end_date: '' } })
        setPage(1)
    }

    const isAnyFilterActive = searchQuery || statusFilter || filters?.rangDate?.start_date

    // Debounce search input
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            setSearchDebounce(searchQuery)
            return
        }
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allBookings({
            page,
            limit,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
            date_from: filters?.rangDate?.start_date ?? null,
            date_to: filters?.rangDate?.end_date ?? null,
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce, filters])

    const handleView = (bookingId) => {
        navigate('/bookings/view', {
            state: { bookingId }
        })
    }

    // Delete handler
    const handleDelete = async (bookingId) => {
        const result = await Swal.fire({
            title: 'Delete Booking?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteBookings({
                id: bookingId
            }))

            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500,
                    showConfirmButton: false,
                })
            }

            dispatch(
                allBookings({
                    page,
                    limit,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined,
                    date_from: filters?.rangDate?.start_date ?? null,
                    date_to: filters?.rangDate?.end_date ?? null,
                })
            )
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || 'Failed to delete booking.',
            })
        }
    }

    // Get status badge styling
    const getStatusBadge = (status) => {
        const statusConfig = {
            ongoing: { bg: '#28a745', text: 'Ongoing' },
            completed: { bg: '#17a2b8', text: 'Completed' },
            cancelled: { bg: '#dc3545', text: 'Cancelled' },
            pending: { bg: '#ffc107', text: 'Pending' },
            scheduled: { bg: '#007bff', text: 'Scheduled' }
        }

        const config = statusConfig[status] || { bg: '#6c757d', text: status }

        return (
            <span
                style={{
                    backgroundColor: config.bg,
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    display: 'inline-block'
                }}
            >
                {config.text}
            </span>
        )
    }

    // Get acceptance status badge
    const getAcceptanceBadge = (isAccepted) => {
        if (isAccepted === 1) {
            return (
                <span
                    style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-block'
                    }}
                >
                    Accepted
                </span>
            )
        } else if (isAccepted === 0) {
            return (
                <span
                    style={{
                        backgroundColor: '#ffc107',
                        color: '#000',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-block'
                    }}
                >
                    Pending
                </span>
            )
        }
        return 'NA'
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((bookingList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Session ID',
            selector: row => row.booking_id,
            sortable: true,
            minWidth: '160px',
            cell: row => (
                <span style={{ fontWeight: '600', color: '#495057' }}>
                    {row.booking_id || 'NA'}
                </span>
            )
        },
        {
            name: 'Booking ID',
            selector: row => row.order.order_id,
            sortable: true,
            minWidth: '160px',
            cell: row => (
                <span style={{ fontWeight: '600', color: '#495057' }}>
                    {row.order.order_id || 'NA'}
                </span>
            )
        },
        {
            name: 'Tutor',
            minWidth: '200px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <img
                        src={row.tutor?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="tutor"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                        }}
                    />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>
                            {`${row.tutor?.first_name || ''} ${row.tutor?.last_name || ''}`.trim() || 'NA'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            {row.tutor?.email || ''}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Parent',
            minWidth: '200px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <img
                        src={row.parent?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="parent"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                        }}
                    />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>
                            {`${row.parent?.first_name || ''} ${row.parent?.last_name || ''}`.trim() || 'NA'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            {row.parent?.email || ''}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Start Time',
            selector: row =>
                row.start_time
                    ? row.start_time
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'End Time',
            selector: row =>
                row.end_time
                    ? row.end_time
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Duration',
            width: '100px',
            cell: row => {
                return "1 hr"
                // if (row.start_time && row.end_time) {
                //     const start = moment(row.start_time)
                //     const end = moment(row.end_time)
                //     const duration = moment.duration(end.diff(start))
                //     const hours = Math.floor(duration.asHours())
                //     const minutes = duration.minutes()
                //     return `${hours}h ${minutes}m`
                // }
                // return 'NA'
            }
        },
        {
            name: 'Status',
            width: '120px',
            center: true,
            cell: row => getStatusBadge(row.status),
        },
        {
            name: 'Acceptance',
            width: '120px',
            center: true,
            cell: row => getAcceptanceBadge(row.is_accepted),
        },
        {
            name: 'Rescheduled',
            width: '120px',
            center: true,
            cell: row => (
                row.is_rescheduled === 1 ? (
                    <span style={{ color: '#ffc107' }}>
                        <i className="fe-refresh-cw" style={{ fontSize: '16px' }} />
                    </span>
                ) : (
                    <span style={{ color: '#6c757d' }}>-</span>
                )
            ),
        },
        {
            name: 'Premium',
            width: '100px',
            center: true,
            cell: row => (
                row.tutor?.is_premium_verified === 1 ? (
                    <span
                        style={{
                            backgroundColor: '#ffd700',
                            color: '#000',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        <i className="fe-star" style={{ fontSize: '10px' }} />
                        Premium
                    </span>
                ) : (
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Standard</span>
                )
            ),
        },
        {
            name: 'Created Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A')
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Action',
            width: '120px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('bookings', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('bookings', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dc3545',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '48px',
            },
        },
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#212529',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
            stripedStyle: {
                backgroundColor: '#ffffff',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
                fontSize: '13px',
            },
        },
    }

    const handleDownloadExcel = async () => {
        try {
            const request = {
                status: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchDebounce || undefined,
                is_report: true,
            };

            if (filters === 'custom' && filters?.rangDate?.start_date && filters?.rangDate?.end_date) {
                request.date_from = moment(filters.rangDate.start_date).format('YYYY-MM-DD');
                request.date_to = moment(filters.rangDate.end_date).format('YYYY-MM-DD');
            }

            const rData = await bookingListing(request);

            if (Number(rData.code) === 200) {

                const columns = [
                    { header: 'Booking ID', key: 'booking_id', width: 18, horizontal: 'center' },
                    { header: 'Session ID', key: 'session_id', width: 18, horizontal: 'center' },
                    { header: 'Session Date', key: 'session_date', width: 15, horizontal: 'center' },
                    { header: 'Day', key: 'day', width: 12, horizontal: 'center' },
                    { header: 'Start Time', key: 'start_time', width: 12, horizontal: 'center' },
                    { header: 'End Time', key: 'end_time', width: 12, horizontal: 'center' },
                    { header: 'Session Number', key: 'session_number', width: 15, horizontal: 'center' },
                    { header: 'Status', key: 'status', width: 15, horizontal: 'center' },
                    // Tutor
                    { header: 'Tutor Name', key: 'tutor_name', width: 22, horizontal: 'center' },
                    { header: 'Tutor Email', key: 'tutor_email', width: 28, horizontal: 'center' },
                    { header: 'Tutor Phone', key: 'tutor_phone', width: 18, horizontal: 'center' },
                    { header: 'Premium Verified', key: 'is_premium', width: 16, horizontal: 'center' },
                    // Parent
                    { header: 'Parent Name', key: 'parent_name', width: 22, horizontal: 'center' },
                    { header: 'Parent Email', key: 'parent_email', width: 28, horizontal: 'center' },
                    { header: 'Parent Phone', key: 'parent_phone', width: 18, horizontal: 'center' },
                    // Order / Pack
                    { header: 'Order ID', key: 'order_ref', width: 16, horizontal: 'center' },
                    { header: 'Session Type', key: 'session_type', width: 14, horizontal: 'center' },
                    { header: 'Total Sessions', key: 'total_sessions', width: 14, horizontal: 'center' },
                    { header: 'Sessions Used', key: 'sessions_used', width: 14, horizontal: 'center' },
                    { header: 'Sessions Remaining', key: 'sessions_remaining', width: 18, horizontal: 'center' },
                    { header: 'Price/Session (AED)', key: 'price_per_session', width: 20, horizontal: 'center' },
                    { header: 'Total Amount (AED)', key: 'total_amount', width: 20, horizontal: 'center' },
                    { header: 'Commission (%)', key: 'commission_rate', width: 15, horizontal: 'center' },
                    { header: 'Payment Status', key: 'payment_status', width: 15, horizontal: 'center' },
                    { header: 'Payment Method', key: 'payment_method', width: 15, horizontal: 'center' },
                    { header: 'Order Status', key: 'order_status', width: 14, horizontal: 'center' },
                    { header: 'Purchased At', key: 'purchased_at', width: 18, horizontal: 'center' },
                    { header: 'Order Expires At', key: 'expires_at', width: 18, horizontal: 'center' },
                    // Cancellation
                    { header: 'Cancelled By', key: 'cancelled_by', width: 14, horizontal: 'center' },
                    { header: 'Cancel Reason', key: 'cancellation_reason', width: 28, horizontal: 'center' },
                    { header: 'Cancelled At', key: 'cancelled_at', width: 18, horizontal: 'center' },
                    // Timestamps
                    { header: 'Booking Created At', key: 'created_at', width: 20, horizontal: 'center' },
                ];

                const ExportData = rData.data.map((row) => ({
                    booking_id: row.order.order_id || '-',
                    session_id: row.booking_id || '-',
                    session_date: row.session_date || '-',
                    day: row.day
                        ? row.day.charAt(0).toUpperCase() + row.day.slice(1)
                        : '-',
                    start_time: row.start_time || '-',
                    end_time: row.end_time || '-',
                    session_number: row.session_number ?? '-',
                    status: row.status
                        ? row.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                        : '-',
                    // Tutor
                    tutor_name: row.tutor
                        ? `${row.tutor.first_name} ${row.tutor.last_name}`
                        : '-',
                    tutor_email: row.tutor?.email || '-',
                    tutor_phone: row.tutor
                        ? `${row.tutor.country_code} ${row.tutor.phone_number}`
                        : '-',
                    is_premium: row.tutor?.is_premium_verified ? 'Yes' : 'No',
                    // Parent
                    parent_name: row.parent
                        ? `${row.parent.first_name} ${row.parent.last_name}`
                        : '-',
                    parent_email: row.parent?.email || '-',
                    parent_phone: row.parent
                        ? `${row.parent.country_code} ${row.parent.phone_number}`
                        : '-',
                    // Order
                    order_ref: row.order?.order_id || '-',
                    session_type: row.order?.session_type
                        ? row.order.session_type.replace(/_/g, ' ').toUpperCase()
                        : '-',
                    total_sessions: row.order?.total_sessions ?? '-',
                    sessions_used: row.order?.sessions_used ?? '-',
                    sessions_remaining: row.order?.sessions_remaining ?? '-',
                    price_per_session: row.order?.price_per_session ?? '-',
                    total_amount: row.order?.total_amount ?? '-',
                    commission_rate: row.order?.commission_rate ?? '-',
                    payment_status: row.order?.payment_status
                        ? row.order.payment_status.charAt(0).toUpperCase() + row.order.payment_status.slice(1)
                        : '-',
                    payment_method: row.order?.payment_method
                        ? row.order.payment_method.toUpperCase()
                        : '-',
                    order_status: row.order?.status
                        ? row.order.status.charAt(0).toUpperCase() + row.order.status.slice(1)
                        : '-',
                    purchased_at: row.order?.purchased_at
                        ? moment(row.order.purchased_at).format('YYYY-MM-DD HH:mm')
                        : '-',
                    expires_at: row.order?.expires_at
                        ? moment(row.order.expires_at).format('YYYY-MM-DD')
                        : '-',
                    // Cancellation
                    cancelled_by: row.cancelled_by || '-',
                    cancellation_reason: row.cancellation_reason || '-',
                    cancelled_at: row.cancelled_at
                        ? moment(row.cancelled_at).format('YYYY-MM-DD HH:mm')
                        : '-',
                    // Timestamps
                    created_at: row.createdAt
                        ? moment(row.createdAt).format('YYYY-MM-DD HH:mm')
                        : '-',
                }));

                const fileName = `Booking_Report_${moment().format('DDMMYYYYHmmss')}`;
                exportExcelFile(columns, ExportData, fileName);

                Swal.fire({
                    icon: 'success',
                    title: 'Downloaded',
                    text: 'Booking report downloaded successfully',
                    timer: 1500,
                    showConfirmButton: false,
                });

            } else {
                Swal.fire({ icon: 'error', title: 'Failed', text: rData?.message });
            }

        } catch (error) {
            console.error(error);
            Swal.fire({ icon: 'error', title: 'Failed', text: 'Something went wrong!' });
        }
    };


    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Bookings</h4>
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">

                        {/* Left: Search */}
                        <div className="position-relative">
                            <i
                                className="fe-search position-absolute"
                                style={{
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d',
                                    fontSize: '16px'
                                }}
                            />
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by booking ID, tutor name, parent name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    paddingLeft: '38px',
                                    paddingRight: searchQuery ? '38px' : '12px',
                                    height: '40px',
                                    minWidth: '400px',
                                    fontSize: '14px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            />
                            {searchQuery && (
                                <button
                                    className="btn btn-link position-absolute p-0"
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    style={{
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#6c757d'
                                    }}
                                >
                                    <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                </button>
                            )}
                        </div>

                        {/* Right: Filter dropdown + Download Report */}
                        <div className="d-flex align-items-center gap-2">

                            {/* Status Filter */}
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    setPage(1)
                                }}
                                style={{
                                    height: '40px',
                                    width: '130px',
                                    fontSize: '13px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="upcoming">Upcoming</option>
                                <option value="ongoing">Ongoing</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="rescheduled">Rescheduled</option>
                                <option value="expired">Expired</option>
                            </select>

                            {/* Date Range Picker for report download */}
                            <DateRangePicker
                                onApply={setDates}
                                initialSettings={{
                                    startDate: moment().subtract(6, 'days').format('MM/DD/YYYY'),
                                    endDate: moment().format('MM/DD/YYYY'),
                                    ranges: Globals.datePickerRanges,
                                    autoUpdateInput: false,
                                }}
                            >
                                <input
                                    type="text"
                                    readOnly
                                    value={
                                        filters?.rangDate.start_date !== '' && filters?.rangDate.end_date !== ''
                                            ? moment(filters?.rangDate.start_date).format('MMM DD, YYYY') + ' – ' + moment(filters?.rangDate.end_date).format('MMM DD, YYYY')
                                            : 'Select Date Range'
                                    }
                                    className="form-control"
                                    style={{
                                        height: '40px',
                                        width: '190px',
                                        fontSize: '13px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        backgroundColor: '#fff',
                                        color: filters?.rangDate.start_date ? '#212529' : '#6c757d',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                />
                            </DateRangePicker>


                            {/* Clear Filters Button */}
                            {isAnyFilterActive && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger d-flex align-items-center gap-1 reset-filter-btn"
                                    onClick={handleResetFilters}
                                    title="Clear all filters"
                                    style={{
                                        height: '40px',
                                        fontSize: '13px',
                                        borderRadius: '6px',
                                        whiteSpace: 'nowrap',
                                        paddingLeft: '12px',
                                        paddingRight: '12px',
                                        flexShrink: 0,
                                        border: '1px solid #dee2e6',
                                        color: '#6c757d',
                                    }}
                                >
                                    <i className="fe-x" style={{ fontSize: '14px' }} />
                                    Clear Filters
                                </button>
                            )}

                            {/* Download Report Button */}
                            <button
                                type="button"
                                className="btn btn-primary d-flex align-items-center gap-2"
                                onClick={handleDownloadExcel}
                                disabled={isLoading || bookingList?.data?.length === 0}
                                style={{
                                    height: '40px',
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    whiteSpace: 'nowrap',
                                    paddingLeft: '14px',
                                    paddingRight: '14px',
                                    flexShrink: 0,
                                }}
                            >
                                {isLoading && bookingList?.data?.length > 0 ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="ti ti-download" style={{ fontSize: '16px' }} />
                                        Download Report
                                    </>
                                )}
                            </button>

                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={bookingList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={bookingList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={bookingList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={bookingList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-calendar" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No bookings found matching your search' : 'No bookings found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Bookings will appear here once created'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}

                />
            </div>
        </div>
    )
}

export default BookingList;